import axios from 'axios'
import ParamsUtil from 'utils/params-util'

const API_PATH = 'api/v2/admins/consignor_settlements'

const cleanParams = (params = {}) => {
  if (ParamsUtil.removeEmptyAttributes) {
    return ParamsUtil.removeEmptyAttributes(params) || {}
  }

  return Object.fromEntries(
    Object.entries(params).filter(([ , value ]) => value !== '' && value != null)
  )
}

const uploadFiles = async (url, files = []) => {
  const formData = new FormData()
  files.forEach((file) => formData.append('files[]', file))

  return axios.post(url, formData)
}

const ConsignorSettlementsApiV2 = {
  removeNegotiationProof: (id, attachmentId) => axios.delete(`${API_PATH}/${id}/negotiation_proofs/${attachmentId}`),
  saveAndSubmit: (id, payload, files = []) => {
    const form = new FormData()
    Object.entries(payload).forEach(([key, value]) => form.append(key, value == null ? '' : value))
    files.forEach((file) => form.append('files[]', file))
    return axios.post(`${API_PATH}/${id}/save_and_submit`, form)
  },
  notificationCounts: () => axios.get(`${API_PATH}/notification_counts`),
  get: async (params = {}) => {
    return axios.get(API_PATH, {
      params: cleanParams(params)
    })
  },

  getTransferDetail: (id) => axios.get(`${API_PATH}/transfers/${id}`),

  getDetail: async (id) => {
    return axios.get(`${API_PATH}/${id}`)
  },

  saveNegotiation: async (id, payload = {}) => {
    return axios.patch(
      `${API_PATH}/${id}/negotiation`,
      {
        consignor_settlement: payload
      }
    )
  },

  uploadNegotiationProofs: async (id, files = []) => {
    return uploadFiles(`${API_PATH}/${id}/negotiation_proofs`, files)
  },

  uploadIdentityVerificationProofs: async (id, files = []) => {
    return uploadFiles(
      `${API_PATH}/${id}/negotiation_proofs?proof_kind=identity_verification`,
      files
    )
  },

  requestFinance: async (id, paymentId) => {
    return axios.post(`${API_PATH}/${id}/request_finance`, { payment_id: paymentId })
  },

  confirmFinance: async (id, note = '', paymentId) => {
    return axios.post(`${API_PATH}/${id}/confirm_finance`, { note, payment_id: paymentId })
  },

  rejectFinanceConfirmation: async (id, note = '', paymentId) => {
    return axios.post(`${API_PATH}/${id}/reject_finance_confirmation`, { note, payment_id: paymentId })
  },

  rejectApprovedFinance: async (id, note, paymentId) => {
    return axios.post(`${API_PATH}/${id}/reject_approved_finance`, { note, payment_id: paymentId })
  },

  connectUserOptions: (id, q) => axios.get(`${API_PATH}/${id}/connect_user_options`, { params: { q } }),

  connectUser: (id, payload) => axios.post(`${API_PATH}/${id}/connect_user`, payload),

  assignCurator: async (id, productCuratorPic) => {
    return axios.patch(`${API_PATH}/${id}/assign_curator`, {
      product_curator_pic: productCuratorPic
    })
  },

  assignSourcingBuyer: async (id, sourcedBy) => {
    return axios.patch(`${API_PATH}/${id}/assign_sourcing_buyer`, {
      sourced_by: sourcedBy
    })
  },

  uploadTransferProofs: async (id, files = []) => {
    return uploadFiles(`${API_PATH}/${id}/transfer_proofs`, files)
  },

  cancelPayment: (id, paymentId) => axios.post(`${API_PATH}/${id}/cancel_payment`, { payment_id: paymentId }),

  savePayment: async (id, payload) => axios.post(`${API_PATH}/${id}/save_payment`, payload),

  savePaymentTransfer: async (id, paymentId, files = [], onUploadProgress) => {
    const data = new FormData()
    data.append('payment_id', paymentId)
    data.append('compact_response', '1')
    files.forEach((file) => data.append('files[]', file, file.name || 'transfer-proof'))
    return axios.post(`${API_PATH}/${id}/save_payment_transfer`, data, { timeout: 120000, onUploadProgress })
  },

  revisePaymentProofs: (id, paymentId, payload, onUploadProgress) => {
    const data = new FormData()
    data.append('payment_id', paymentId)
    data.append('reason', payload.reason)
    data.append('expected_updated_at', payload.expected_updated_at)
    payload.expected_proof_ids.forEach((value) => data.append('expected_proof_ids[]', value))
    payload.remove_ids.forEach((value) => data.append('remove_ids[]', value))
    payload.files.forEach((file) => data.append('files[]', file, file.name || 'transfer-proof'))
    return axios.post(`${API_PATH}/${id}/revise_payment_proofs`, data, { timeout: 120000, onUploadProgress })
  },

  paymentTransferStatus: (id, paymentId) => axios.get(`${API_PATH}/${id}/payment_transfer_status`, { params: { payment_id: paymentId }, timeout: 30000 }),

  markPaymentMaker: (id, paymentId, note) => axios.post(`${API_PATH}/${id}/mark_payment_maker`, { payment_id: paymentId, note }),

  sendPaymentEmail: async (id, paymentId, payload = {}) => axios.post(`${API_PATH}/${id}/send_payment_email`, { ...payload, payment_id: paymentId }),

  markEmailDelivered: (id, paymentId, payload) => axios.post(`${API_PATH}/${id}/mark_email_delivered`, { ...payload, payment_id: paymentId }),

  markTransferredManually: (id, note) => axios.post(`${API_PATH}/${id}/mark_transferred_manually`, { note }),

  bulkMarkTransferredManually: (ids, note) => axios.post(`${API_PATH}/bulk_mark_transferred_manually`, { ids, note }),

  markTransferred: async (id) => {
    return axios.post(`${API_PATH}/${id}/mark_transferred`)
  },

  updateEmailRecipient: async (id, emailRecipient) => {
    return axios.patch(`${API_PATH}/${id}/email_recipient`, {
      email_recipient: emailRecipient
    })
  },

  sendTransferEmail: async (id) => {
    return axios.post(`${API_PATH}/${id}/send_transfer_email`)
  },

  syncEligible: async (payload = {}) => {
    return axios.post(`${API_PATH}/sync_eligible`, payload)
  },

  exportCsv: async (params = {}) => {
    return axios.get(`${API_PATH}/export_csv`, {
      params: cleanParams(params),
      responseType: 'blob'
    })
  }
}

export default ConsignorSettlementsApiV2
